import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Crosshair } from "@/components/Crosshair";
import { Plus, Pencil, Check, X, ArrowUp, ArrowDown, Archive, RotateCcw, Loader2, MessagesSquare } from "lucide-react";

const BTN = "font-mono uppercase text-[10px] font-bold px-2.5 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1";
const INPUT = "bg-input border border-sniper focus:border-olive outline-none px-2.5 py-1.5 text-foreground font-body text-sm";

export default function AdminForum() {
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, retired: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/forum/categories");
      setCats(data.categories || []);
      setCounts(data.counts || { all: 0, active: 0, retired: 0 });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.post("/admin/forum/categories", { label });
      setNewLabel("");
      toast.success(`Added “${label}”`);
      await load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.patch(`/admin/forum/categories/${id}`, { label });
      setEditId(null); setEditLabel("");
      toast.success("Category renamed");
      await load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (id, action, label) => {
    setBusy(true);
    try {
      await api.post(`/admin/forum/categories/${id}/${action}`);
      toast.success(action === "retire" ? `Archived “${label}”` : `Restored “${label}”`);
      await load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  // Reorder among ACTIVE categories only (retired don't appear in the filter row).
  const move = async (id, dir) => {
    const active = cats.filter((c) => c.status !== "retired");
    const idx = active.findIndex((c) => c.category_id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= active.length) return;
    const reordered = [...active];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    const ordered_ids = reordered.map((c) => c.category_id);
    // optimistic
    setCats((prev) => {
      const retired = prev.filter((c) => c.status === "retired");
      return [...reordered, ...retired];
    });
    setBusy(true);
    try {
      await api.post("/admin/forum/categories/reorder", { ordered_ids });
      await load();
    } catch (e) { toast.error(formatError(e)); await load(); } finally { setBusy(false); }
  };

  const active = cats.filter((c) => c.status !== "retired");
  const retired = cats.filter((c) => c.status === "retired");

  return (
    <div data-testid="admin-forum">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2">
            <MessagesSquare className="w-6 h-6 text-olive-bright" /> Forum Categories
          </h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            Add, rename, reorder & archive the categories in the forum filter row. Archiving keeps existing threads' tags — it just hides the category from new posts.
          </p>
        </div>
      </div>

      <Crosshair className="my-5" />

      {/* Add */}
      <div className="flex items-end gap-2 mb-5" data-testid="forum-cat-add-row">
        <div className="flex-1 max-w-sm">
          <label className="block font-mono text-[10px] uppercase tracking-[1.5px] text-secondary-foreground mb-1.5">New category</label>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="e.g. Optics Q&amp;A"
            className={`${INPUT} w-full`}
            data-testid="forum-cat-add-input"
          />
        </div>
        <button onClick={add} disabled={busy || !newLabel.trim()} data-testid="forum-cat-add-btn"
          className={`${BTN} bg-olive hover:bg-olive-bright text-white`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      <div className="flex items-center gap-4 mb-3 font-mono text-[11px] uppercase text-muted-foreground" data-testid="forum-cat-counts">
        <span data-testid="forum-cat-count-active">{counts.active} active</span>
        <span data-testid="forum-cat-count-retired">{counts.retired} archived</span>
      </div>

      {loading ? (
        <p className="font-mono text-sm uppercase text-muted-foreground flex items-center gap-2" data-testid="forum-cat-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : (
        <>
          <div className="border border-sniper divide-y divide-sniper" data-testid="forum-cat-active-list">
            {active.length === 0 ? (
              <div className="px-4 py-6 text-center font-mono text-sm text-muted-foreground" data-testid="forum-cat-empty">No active categories.</div>
            ) : active.map((c, i) => (
              <div key={c.category_id} className="flex items-center gap-3 px-3 sm:px-4 py-3 bg-surface" data-testid={`forum-cat-row-${c.slug}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(c.category_id, -1)} disabled={busy || i === 0} data-testid={`forum-cat-up-${c.slug}`}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move up"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(c.category_id, 1)} disabled={busy || i === active.length - 1} data-testid={`forum-cat-down-${c.slug}`}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move down"><ArrowDown className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  {editId === c.category_id ? (
                    <div className="flex items-center gap-2">
                      <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(c.category_id); if (e.key === "Escape") { setEditId(null); } }}
                        autoFocus className={`${INPUT} flex-1 max-w-xs`} data-testid={`forum-cat-rename-input-${c.slug}`} />
                      <button onClick={() => saveRename(c.category_id)} disabled={busy} data-testid={`forum-cat-rename-save-${c.slug}`} className={`${BTN} border border-olive text-olive-bright hover:bg-olive/10`}><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setEditId(null); setEditLabel(""); }} data-testid={`forum-cat-rename-cancel-${c.slug}`} className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-body font-bold text-foreground truncate" data-testid={`forum-cat-label-${c.slug}`}>{c.label}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">/{c.slug}</span>
                    </div>
                  )}
                </div>
                {editId !== c.category_id && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => { setEditId(c.category_id); setEditLabel(c.label); }} disabled={busy} data-testid={`forum-cat-edit-${c.slug}`} className={`${BTN} border border-sniper text-secondary-foreground hover:text-foreground hover:border-olive/60`}><Pencil className="w-3.5 h-3.5" /> Rename</button>
                    <button onClick={() => setStatus(c.category_id, "retire", c.label)} disabled={busy} data-testid={`forum-cat-retire-${c.slug}`} className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10`}><Archive className="w-3.5 h-3.5" /> Archive</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {retired.length > 0 && (
            <div className="mt-6">
              <h2 className="font-mono text-[11px] uppercase tracking-[1.5px] text-muted-foreground mb-2">Archived</h2>
              <div className="border border-sniper divide-y divide-sniper" data-testid="forum-cat-retired-list">
                {retired.map((c) => (
                  <div key={c.category_id} className="flex items-center gap-3 px-3 sm:px-4 py-3 bg-surface/60" data-testid={`forum-cat-row-${c.slug}`}>
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="font-body text-muted-foreground line-through truncate" data-testid={`forum-cat-label-${c.slug}`}>{c.label}</span>
                      <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border border-blaze/40 text-blaze" data-testid={`forum-cat-status-${c.slug}`}>archived</span>
                    </div>
                    <button onClick={() => setStatus(c.category_id, "restore", c.label)} disabled={busy} data-testid={`forum-cat-restore-${c.slug}`} className={`${BTN} border border-olive text-olive-bright hover:bg-olive/10`}><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
